// DemoOnboardingPreview.tsx
// Phase 5 (2026-05-25) — read-only preview showing how onboarding fields
// would render for the current user. Used to validate field architecture
// before the real onboarding UI lands in Phase 12.
import React from 'react';
import { useAuthenticatedUser } from '../hooks/useAuthenticatedUser';
import { ONBOARDING_FIELDS, onboardingFieldStates } from '../lib/onboardingFields';
import { FieldRow } from './FieldRow';
import { MockupPreviewBanner } from './MockupPreviewBanner';

export function DemoOnboardingPreview() {
  const u = useAuthenticatedUser();
  const states = onboardingFieldStates(u.userStatus);
  const enabledOrOptional = ONBOARDING_FIELDS.filter((f) => {
    const s = states[f.id];
    return s === 'enabled' || s === 'optional';
  });
  const disabled = ONBOARDING_FIELDS.filter((f) => states[f.id] === 'disabled');
  const hiddenIds = ONBOARDING_FIELDS.filter((f) => states[f.id] === 'hidden').map((f) => f.id);

  return (
    <div className="app-container py-8 space-y-6" data-testid="demo-onboarding-preview">
      <MockupPreviewBanner screen='demo-onboarding-preview' />
      <div>
        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Onboarding Architecture · Phase 5</div>
        <h2 className="text-2xl font-black uppercase italic mt-1">Field Preview · {u.accountStatusLabel}</h2>
        <p className="text-xs text-[var(--muted-text)] font-bold mt-1">
          This screen is a read-only preview of which onboarding fields would be presented to your account
          type. Real onboarding UI lands in Phase 12. Sensitive fields are hidden or locked until real-account
          onboarding is enabled by compliance.
        </p>
      </div>

      <section data-testid="fields-enabled-section">
        <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600 mb-2">
          Enabled ({enabledOrOptional.length})
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {enabledOrOptional.map((f) => (
            <FieldRow key={f.id} field={f} status={u.userStatus} />
          ))}
        </div>
      </section>

      <section data-testid="fields-disabled-section">
        <div className="text-[10px] font-black uppercase tracking-widest text-amber-600 mb-2">
          Disabled / Locked ({disabled.length})
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {disabled.map((f) => (
            <FieldRow key={f.id} field={f} status={u.userStatus} />
          ))}
        </div>
      </section>

      <section data-testid="fields-hidden-section" className="opacity-60">
        <div className="text-[10px] font-black uppercase tracking-widest text-rose-600 mb-2">
          Hidden ({hiddenIds.length}) — never rendered, never collected
        </div>
        <div className="text-[11px] font-bold text-[var(--muted-text)]">
          {hiddenIds.length === 0 ? 'None' : hiddenIds.join(', ')}
        </div>
      </section>
    </div>
  );
}

export default DemoOnboardingPreview;
